import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Helmet from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Alert, Button, Input } from 'vendor/heroui';
import { Container } from 'components/HeroSemantic';
import { ThemeHeader } from 'components';
import TwoFactorTypeForm from 'components/Form/TwoFactorTypeForm';
import TwoFactorAuthForm from 'components/Form/TwoFactorAuthForm';
import usePush from 'hooks/router/usePush';
import usePasskeyStepUp from 'hooks/Auth/usePasskeyStepUp';
import useRecoveryStatusFetch from 'hooks/Auth/useRecoveryStatusFetch';
import passkeyErrorMessageId from 'utils/auth/passkeyErrorMessageId';
import { clearSelected2FADeliveryMethod, markSessionVerified } from 'state/modules/auth';
import { display } from 'state/modules/dialog';
import {AUTH_MODES,
  requestStepUpSmsCode,
  requiresTwoFactor,
  verifyOwnerPassword,
  verifyRecoveryCode,
  verifyStepUpSmsCode} from 'state/modules/verification';

const VIEWS = {
  CHOOSE: 'choose',
  PASSWORD: 'password',
  RECIPIENT: 'recipient',
  SMS: 'sms',
  RECOVERY: 'recovery',
};

const FALLBACK_NEXT = '/financial/currentStatus';

// Strips a "/en/" or similar locale prefix from a pathname so the verification
// grant matches the way `isProtectedPath` normalises routes for its check.
// Only 2-3 letter locale segments — a naive [a-z]+ would swallow single-word
// paths like "/security" too, breaking the guard comparison.
const stripLocale = pathname => (pathname || '').replace(/^\/[a-z]{2,3}(?=\/|$)/i, '') || '/';

const buttonClassName = [
  'w-full justify-start gap-3 rounded-xl border border-[var(--owner-border)] bg-[var(--owner-surface)]',
  'px-4 py-3 text-left text-[var(--owner-text)] shadow-sm transition',
  'hover:border-[var(--tenant-primary)] hover:bg-[color-mix(in_srgb,var(--tenant-primary)_8%,var(--owner-surface))]',
].join(' ');

const VerificationRequired = () => {
  const { formatMessage } = useIntl();
  const location = useLocation();
  const dispatch = useDispatch();
  const { push } = usePush();
  const tenantId = useSelector(state => state.tenant.id);
  // Owner is already logged in at /verify — pull the username from the
  // active session so the step-up login call can identify them without a
  // second username input.
  const username = useSelector(state => state.auth?.user?.username);
  // The password path is a password+SMS-2FA path (name reflects that). Only
  // owners with 2FA enabled on their account see it; a no-2FA owner would
  // need to fall back to passkey or recovery code.
  const userHasTwoFactor = useSelector(state => state.auth?.user?.twoFactorAuth === true);
  // OWN-230: gate the Passkey/Recovery options on whether they're actually
  // usable for this account, instead of always showing them and letting the
  // owner hit a confusing failure. hasPasskey is already reliably populated
  // at login time. recoveryCodesRemaining is NOT guaranteed loaded here -
  // App.js only fetches it when hasPasskey is true (see the effect below),
  // which would leave it unset for the exact edge case this ticket cares
  // about: an owner with recovery codes but zero passkeys.
  const hasPasskey = useSelector(state => state.auth?.hasPasskey);
  const recoveryCodesRemaining = useSelector(state => state.auth?.recoveryCodesRemaining);
  const { authenticate, isBusy, error: passkeyError } = usePasskeyStepUp();
  const fetchRecoveryStatus = useRecoveryStatusFetch();

  // Single source of truth per method, shared by the combined "all
  // unavailable" check and each option's own branch below - keeps them from
  // drifting out of sync if a method's availability rule ever changes.
  // recoveryAvailable is `!== 0` (not a strict positive check) so it reads as
  // available while recoveryCodesRemaining is still loading/unknown (null),
  // and only flips once we positively know it's zero.
  const passkeyAvailable = Boolean(hasPasskey);
  const recoveryAvailable = recoveryCodesRemaining !== 0;
  // Password-only step-up: the owner's sole possible method when they have
  // no passkey, no recovery codes, AND no 2FA - normally password+2FA is
  // the only password path (gated on userHasTwoFactor below), leaving an
  // owner in this exact combination with no way to verify at all. Distinct
  // from the ordinary password+2FA button, this one succeeds on password
  // alone (no SMS step) and DOES grant sessionVerified (see grantAndGo)
  // since there's no other method this session could have used instead.
  const passwordOnlyAvailable = !passkeyAvailable && !userHasTwoFactor && !recoveryAvailable;
  // Was previously the "nothing works" case - passwordOnlyAvailable now
  // covers it, so noMethodAvailable is unreachable today but kept for the
  // (currently impossible) future where password-only step-up is itself
  // disabled/removed - it should still fail closed, not silently pass.
  const noMethodAvailable = !passkeyAvailable && !userHasTwoFactor && !recoveryAvailable && !passwordOnlyAvailable;

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const rawNext = params.get('next') || FALLBACK_NEXT;
  // Split off query + hash before locale-stripping so the authorization key
  // stays pathname-only. The comparison in RenderRoute uses location.pathname
  // (already query-free) — leaking `?foo=bar` in here would break the match.
  // rawNext is kept intact for push() so query params still reach the destination.
  const nextPathnameOnly = rawNext.split('?')[0].split('#')[0];
  const nextPathname = stripLocale(nextPathnameOnly);
  const securityOnly = requiresTwoFactor(nextPathname);

  const [view, setView] = useState(VIEWS.CHOOSE);
  const [password, setPassword] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [selectedRecipientHash, setSelectedRecipientHash] = useState(null);
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState(null);
  const [twoFactorRequestId, setTwoFactorRequestId] = useState(null);
  const [twoFactorRequestExpiry, setTwoFactorRequestExpiry] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState(null);

  // Common success point for all step-up methods - see AUTH_MODES call sites
  // below. Flips the session-wide sessionVerified flag that gates the
  // protected pages and Security & Login's actions - except for NO_2FA, which is normally a bare password check (no
  // second factor) sitting alongside other real methods and must not grant
  // the same elevated status as those. The one exception is
  // passwordOnlyAvailable: when password is the owner's ONLY possible
  // method (no passkey, no recovery codes, no 2FA), there is no other
  // method this session could have used instead, so it DOES grant
  // sessionVerified - otherwise Security & Login would be permanently
  // unreachable for this owner.
  const grantAndGo = useCallback(authMode => {
    if (authMode !== AUTH_MODES.NO_2FA || passwordOnlyAvailable) {
      dispatch(markSessionVerified());
    }
    push(rawNext);
  }, [dispatch, push, rawNext, passwordOnlyAvailable]);

  const showError = useCallback(message => {
    setInlineError(message);
  }, []);

  useEffect(() => {
    if (passkeyError) {
      const id = passkeyErrorMessageId(passkeyError);
      dispatch(display(
        formatMessage({ id: 'notifications.title.error' }),
        formatMessage({ id }),
        'error',
      ));
    }
  }, [passkeyError, dispatch, formatMessage]);

  // OWN-230: App.js's own recoveryStatus() effect only fires when hasPasskey
  // is true - it never runs at all for an owner with no passkey, so this
  // can't skip fetching outright (that's the exact edge case this ticket
  // cares about). But when hasPasskey IS true, App is already mounted
  // app-wide and fired that fetch as soon as hasPasskey became known
  // (typically at login, well before this screen exists) - dispatching
  // again here would just repeat the identical GET /owners/recovery/status
  // App.js already made. Skip only in that case. Shares App.js's own error
  // handling for this dispatch via useRecoveryStatusFetch.
  useEffect(() => {
    if (!hasPasskey) {
      fetchRecoveryStatus();
    }
  }, [hasPasskey, fetchRecoveryStatus]);

  const handlePasskey = async () => {
    setInlineError(null);
    try {
      const ok = await authenticate();
      if (ok) {
        grantAndGo(AUTH_MODES.PASSKEY);
      }
    }
    catch {
      // Surfaced via the passkeyError effect above.
    }
  };

  const handlePasswordSubmit = async event => {
    event.preventDefault();
    if (!password || submitting) {return;}
    setInlineError(null);
    setSubmitting(true);
    try {
      const result = await dispatch(verifyOwnerPassword(username, password, tenantId));
      // Gateway login returns one of two shapes:
      //   - Owner needs 2FA: { username, auth2faOptions: [{ value, type, hash }, ...] }
      //   - Owner has no 2FA: session-token object with `id` (mongo session id)
      if (Array.isArray(result?.auth2faOptions) && result.auth2faOptions.length > 0) {
        // TwoFactorTypeForm reads state.auth.twoFactorDeliveryMethod from
        // redux as its Send-enable gate. Clear a stale login-2FA selection
        // so the owner must pick a method here before Send lights up.
        dispatch(clearSelected2FADeliveryMethod());
        setRecipients(result.auth2faOptions);
        setView(VIEWS.RECIPIENT);
        return;
      }
      // No-2FA path — allowed for bank/payment (backend keeps submission
      // in pending state) but Security & Login is normally off-limits.
      // Exception: passwordOnlyAvailable means this owner has no passkey or
      // recovery codes either, so password is their only possible method -
      // blocking Security & Login here would be a permanent dead end, same
      // reasoning as the passkey-bootstrap exemption elsewhere.
      if (securityOnly && !passwordOnlyAvailable) {
        showError(formatMessage({ id: 'page.verify.error.securityRequires2fa' }));
        return;
      }
      grantAndGo(AUTH_MODES.NO_2FA);
    }
    catch (err) {
      showError(err?.message || formatMessage({ id: 'page.verify.error.invalidPassword' }));
    }
    finally {
      setSubmitting(false);
    }
  };

  // Called by TwoFactorTypeForm's onSubmit — fires when the owner picks a
  // recipient + delivery method and clicks Send. We then trigger the Vonage
  // SMS/voice call via the gateway → owner-api chain and advance to the
  // code-entry view.
  const handleRecipientSubmit = async (recipientHash, deliveryMethod) => {
    if (submitting || !recipientHash || !deliveryMethod) {return;}
    setInlineError(null);
    setSubmitting(true);
    try {
      const response = await dispatch(requestStepUpSmsCode(tenantId, recipientHash, deliveryMethod));
      const newTwoFactorRequestId = response?.twoFactorRequestId;
      const newTwoFactorRequestExpiry = response?.twoFactorRequestExpiry;
      if (!newTwoFactorRequestId) {
        throw new Error(formatMessage({ id: 'page.verify.error.sms.requestFailed' }));
      }
      setSelectedRecipientHash(recipientHash);
      setSelectedDeliveryMethod(deliveryMethod);
      setTwoFactorRequestId(newTwoFactorRequestId);
      setTwoFactorRequestExpiry(newTwoFactorRequestExpiry);
      setView(VIEWS.SMS);
    }
    catch (err) {
      showError(err?.message || formatMessage({ id: 'page.verify.error.sms.requestFailed' }));
    }
    finally {
      setSubmitting(false);
    }
  };

  // Called by TwoFactorAuthForm — resends the code using the last picked
  // recipient. Same endpoint as the initial send; the owner-api endpoint
  // reuses the still-valid Vonage request or asks for a new one.
  const handleResend = async () => {
    if (submitting || !selectedRecipientHash || !selectedDeliveryMethod) {return;}
    setSubmitting(true);
    try {
      const response = await dispatch(requestStepUpSmsCode(tenantId, selectedRecipientHash, selectedDeliveryMethod));
      const newTwoFactorRequestId = response?.twoFactorRequestId;
      const newTwoFactorRequestExpiry = response?.twoFactorRequestExpiry;
      if (newTwoFactorRequestId) {
        setTwoFactorRequestId(newTwoFactorRequestId);
        setTwoFactorRequestExpiry(newTwoFactorRequestExpiry);
      }
    }
    catch (err) {
      showError(err?.message || formatMessage({ id: 'page.verify.error.sms.requestFailed' }));
    }
    finally {
      setSubmitting(false);
    }
  };

  // Called by TwoFactorAuthForm — verifies the entered code against the
  // Vonage-issued twoFactorRequestId, for the current session owner. Mints an
  // updated token (sessionVerified: true) on success - grantAndGo below also
  // flips the client-side authorization that actually gates the page.
  const handleSmsSubmit = async code => {
    if (!code || submitting || !twoFactorRequestId) {return;}
    setInlineError(null);
    setSubmitting(true);
    try {
      await dispatch(verifyStepUpSmsCode(twoFactorRequestId, code));
      grantAndGo(AUTH_MODES.SMS_2FA);
    }
    catch (err) {
      showError(err?.message || formatMessage({ id: 'page.verify.error.invalidCode' }));
    }
    finally {
      setSubmitting(false);
    }
  };

  const handleRecoverySubmit = async event => {
    event.preventDefault();
    const code = recoveryCode.trim();
    if (!code || submitting) {return;}
    setInlineError(null);
    setSubmitting(true);
    try {
      const result = await dispatch(verifyRecoveryCode(code, tenantId));
      // Defensive: require the explicit boolean `verified: true` from the backend.
      // Rejects false, undefined, null, and truthy non-booleans (e.g. a stray
      // 2xx-with-no-body from a proxy fall-through, or a string "true").
      if (result?.verified !== true) {
        showError(formatMessage({ id: 'page.verify.error.invalidRecovery' }));
        return;
      }
      grantAndGo(AUTH_MODES.RECOVERY_CODE);
    }
    catch (err) {
      showError(err?.message || formatMessage({ id: 'page.verify.error.invalidRecovery' }));
    }
    finally {
      setSubmitting(false);
    }
  };

  const goChoose = () => {
    setView(VIEWS.CHOOSE);
    setInlineError(null);
    setPassword('');
    setRecoveryCode('');
    setRecipients([]);
    setSelectedRecipientHash(null);
    setSelectedDeliveryMethod(null);
    setTwoFactorRequestId(null);
    setTwoFactorRequestExpiry(null);
    // Symmetric with the RECIPIENT-view entry — don't leave a lingering
    // delivery method around after backing out of the flow.
    dispatch(clearSelected2FADeliveryMethod());
  };

  return (
    <Container>
      <Helmet title={formatMessage({ id: 'page.verify.title' })} />
      <div style={{ maxWidth: '480px', margin: '80px auto 40px' }}>
        <ThemeHeader as="h1" titleId="page.verify.title" />

        <p className="mb-6 text-sm text-[var(--owner-text-muted)]">
          <FormattedMessage id="page.verify.description" />
        </p>

        {inlineError && (
          <Alert color="danger" className="mb-4">
            {inlineError}
          </Alert>
        )}

        {view === VIEWS.CHOOSE && (
          <div className="flex flex-col gap-3">
            {/* OWN-230: noMethodAvailable only goes true once recoveryAvailable
                is confirmed false (not still loading/null) - avoids flashing
                this alarming combined message during the brief window before
                the mount effect above resolves. */}
            {noMethodAvailable ? (
              <Alert color="danger" className="text-sm">
                <FormattedMessage
                  id="page.verify.error.allMethodsUnavailable"
                  defaultMessage="No verification methods are available for this account. Please contact our Owners team for help."
                />
              </Alert>
            ) : (
              <>
                {passkeyAvailable ? (
                  <Button type="button" className={buttonClassName} onPress={handlePasskey} isDisabled={isBusy}>
                    <FormattedMessage id="page.verify.option.passkey" defaultMessage="Verify with Passkey" />
                  </Button>
                ) : (
                  <Alert color="warning" className="text-sm">
                    <FormattedMessage
                      id="page.verify.error.noPasskey"
                      defaultMessage="No passkey is registered for this account. Register a passkey to use this verification method."
                    />
                  </Alert>
                )}
                {/* Password path = password + SMS 2FA. Only hidden when the owner
                    has no 2FA on their account (would fail the SMS step anyway) -
                    AC only excludes the NO-2FA case from Security & Login
                    (enforced in handlePasswordSubmit's securityOnly check below),
                    not password+2FA itself. */}
                {userHasTwoFactor && (
                  <Button type="button" className={buttonClassName} onPress={() => setView(VIEWS.PASSWORD)}>
                    <FormattedMessage id="page.verify.option.password" defaultMessage="Verify with Password & 2FA" />
                  </Button>
                )}
                {/* passwordOnlyAvailable: no passkey, no recovery codes, no 2FA -
                    password is the owner's ONLY possible method. Distinct from
                    the button above (no SMS step; succeeds on password alone and
                    grants sessionVerified - see grantAndGo). */}
                {passwordOnlyAvailable && (
                  <Button type="button" className={buttonClassName} onPress={() => setView(VIEWS.PASSWORD)}>
                    <FormattedMessage id="page.verify.option.passwordOnly" defaultMessage="Verify with Password" />
                  </Button>
                )}
                <div className="mt-2 flex justify-center">
                  {recoveryAvailable ? (
                    <Button
                      type="button"
                      variant="light"
                      className="text-sm font-medium text-[var(--owner-text-muted)] underline underline-offset-2 hover:text-[var(--owner-text)]"
                      onPress={() => setView(VIEWS.RECOVERY)}
                    >
                      <FormattedMessage id="page.verify.option.recovery" defaultMessage="Use Backup Recovery Code instead" />
                    </Button>
                  ) : (
                    <p className="text-sm font-medium text-[var(--owner-text-muted)]">
                      <FormattedMessage
                        id="page.verify.error.noRecoveryCodes"
                        defaultMessage="No backup recovery codes are available for this account. Please register a passkey first to generate recovery codes."
                      />
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {view === VIEWS.PASSWORD && (
          <form onSubmit={handlePasswordSubmit} className="flex flex-col gap-3">
            <label htmlFor="verify-password" className="text-sm font-medium text-[var(--owner-text)]">
              <FormattedMessage id="page.verify.password.label" />
            </label>
            <Input
              id="verify-password"
              type="password"
              value={password}
              onChange={event => setPassword(event.target.value)}
              autoComplete="current-password"
              required
            />
            <div className="flex gap-2">
              <Button type="button" variant="light" onPress={goChoose}>
                <FormattedMessage id="general.back" />
              </Button>
              <Button type="submit" color="primary" isDisabled={!password || submitting}>
                <FormattedMessage id="general.continue" />
              </Button>
            </div>
          </form>
        )}
        
        {view === VIEWS.RECIPIENT && (
          <>
            <TwoFactorTypeForm
              recipients={recipients}
              onSubmit={handleRecipientSubmit}
            />
            <div className="mt-3 flex gap-2">
              <Button type="button" variant="light" onPress={goChoose}>
                <FormattedMessage id="general.back" />
              </Button>
            </div>
          </>
        )}

        {view === VIEWS.SMS && (
          <>
            <TwoFactorAuthForm
              twoFactorDeliveryMethod={selectedDeliveryMethod}
              twoFaRequestExpiry={twoFactorRequestExpiry}
              onSubmit={handleSmsSubmit}
              onResend2FACode={handleResend}
            />
            <div className="mt-3 flex gap-2">
              <Button type="button" variant="light" onPress={goChoose}>
                <FormattedMessage id="general.back" />
              </Button>
            </div>
          </>
        )}

        {view === VIEWS.RECOVERY && (
          <form onSubmit={handleRecoverySubmit} className="flex flex-col gap-3">
            <label htmlFor="verify-recovery" className="text-sm font-medium text-[var(--owner-text)]">
              <FormattedMessage id="page.verify.recovery.label" />
            </label>
            <Input
              id="verify-recovery"
              type="text"
              value={recoveryCode}
              onChange={event => setRecoveryCode(event.target.value)}
              autoComplete="one-time-code"
              required
            />
            <div className="flex gap-2">
              <Button type="button" variant="light" onPress={goChoose}>
                <FormattedMessage id="general.back" />
              </Button>
              <Button type="submit" color="primary" isDisabled={!recoveryCode || submitting}>
                <FormattedMessage id="general.verify" />
              </Button>
            </div>
          </form>
        )}
      </div>
    </Container>
  );
};

export default VerificationRequired;
